import i18n from '@/plugins/i18n';
import {
  GetChartOptionsParamType,
  GraphTypesEnum,
  GraphDataNamesEnum,
  apexChartTypes,
  LegendInfoType,
  ChartTypesEnum,
} from '@/components/Common/graph.types';
import {
  XAxisConfig,
  MultiLineToolTip,
  ToolTip,
  YAxisConfig,
  Legend,
  FontColor,
} from '@/components/charts/charts.global.config';
import { formatDate } from '@/utils/time/formatDate';
import { toRef } from 'vue';
import { useStoreData } from '@/components/Common/useStoreData';
import { timeLabels } from '@/components/Main/main.config';
import { getLocalISOTime } from '@/utils/helper-function';

export const getChartOptions = (
  {
    type,
    colors,
    fillOpacity,
    strokeWidth,
    markersSize,
    inverseOrder = false,
    tooltipEnable = true,
    hideCustomToolTip = false,
    annotationRange = [0, 0],
    annotationFill = ANNOTATION_FILL,
    showLegend = true,
    axisLabels,
  }: GetChartOptionsParamType,
  graphType: string,
) => {
  return {
    chart: {
      type,
      animations: {
        enabled: false,
        speed: 500,
      },
      toolbar: {
        show: false,
      },
    },
    grid: {
      show: false,
    },
    colors,
    dataLabels: {
      enabled: false,
    },
    fill: {
      opacity: fillOpacity,
    },
    tooltip: getTooltipOptions(graphType, tooltipEnable, hideCustomToolTip),
    stroke: {
      curve: 'straight',
      width: strokeWidth,
    },
    legend: {
      ...Legend,
      show: showLegend,
      inverseOrder,
      labels: {
        ...Legend.labels,
        colors: FontColor.LIGHT,
      },
      markers: getLegendMarkerOptions(graphType),
    },
    markers: {
      size: markersSize,
      strokeWidth: 0,
      strokeOpacity: 1,
      strokeDashArray: 0,
      fillOpacity: 1,
      shape: 'circle',
      radius: 2,
      offsetX: 0,
      offsetY: 0,
      colors: undefined,
      strokeColors: 'rgb(30,32,33)',
      showNullDataPoints: false,
      hover: {
        size: undefined,
        sizeOffset: 3,
      },
    },
    xaxis: getXAxisOptions(graphType, axisLabels),
    yaxis: {
      ...YAxisConfig,
      labels: {
        ...YAxisConfig.labels,
        formatter: (value) => {
          return Math.round(value * 100) / 100;
        },
      },
      title: {
        text: axisLabels?.y,
        ...YAxisConfig.title,
      },
    },
    annotations: {
      xaxis: [
        {
          x: annotationRange[0],
          x2: annotationRange[1],
          fillColor: annotationFill,
        },
      ],
    },
  };
};

const getLegendMarkerOptions = (graphType) => {
  return graphType === GraphTypesEnum.LINE_CHART ||
    graphType === GraphTypesEnum.LINE_CHART_LABELLED
    ? {
        ...Legend.markers,
      }
    : {
        ...Legend.markers,
        width: 20,
        height: 3,
        radius: 1,
      };
};

const getTooltipOptions = (graphType, tooltipEnable, hideCustomToolTip) => {
  return graphType === GraphTypesEnum.LINE_CHART ||
    graphType === GraphTypesEnum.LINE_CHART_LABELLED
    ? {
        ...ToolTip,
        enabled: tooltipEnable,
      }
    : {
        ...MultiLineToolTip,
        enabled: tooltipEnable,
        custom: hideCustomToolTip ? () => `<div></div>` : customTooltip,
      };
};

const getXAxisOptions = (graphType, axisLabels) => {
  const { intervalTime } = useStoreData();

  return graphType === GraphTypesEnum.LINE_CHART ||
    graphType === GraphTypesEnum.LINE_CHART_LABELLED
    ? {
        tickAmount: intervalTime.value.length > 14 ? 5 : 3,
        ...XAxisConfig,
        title: {
          text: axisLabels.x,
          ...XAxisConfig.title,
          offsetY: 18,
        },
      }
    : {
        ...XAxisConfig,
        title: {
          text: axisLabels.x,
          ...XAxisConfig.title,
          offsetY: 18,
        },
        type: 'datetime',
      };
};

// confidence, critical, major, minor, ground truth, forecast
const anomalyChartColors = [
  '#04D9FF33',
  '#C72D19',
  '#CE5700',
  '#FFDA55',
  '#F59600',
  '#04D9FF',
];

export const chartConfigMapper = {
  [GraphTypesEnum.FORECAST_CHART]: {
    colors: ['#04D9FF33', '#F59600', '#04D9FF'],
    fillOpacity: [0.2, 1, 1],
    markersSize: [0, 0, 0],
    strokeWidth: [0, 1, 1],
  },
  // confidence, critical, major, minor, ground truth, forecast
  [GraphTypesEnum.FORECAST_WITH_ANOMALY]: {
    colors: anomalyChartColors,
    fillOpacity: [0.2, 1, 1, 1, 1, 1],
    markersSize: [0, 5, 5, 5, 0, 0],
    strokeWidth: [0, 2, 2, 2, 1, 1],
  },
  [GraphTypesEnum.LINE_CHART]: {
    colors: ['#F59600', '#2F91EC'],
    fillOpacity: [1],
    markersSize: [4, 4],
    strokeWidth: [4],
  },
  [GraphTypesEnum.LINE_CHART_LABELLED]: {
    colors: ['#F59600', '#2F91EC'],
    fillOpacity: [1],
    markersSize: [4, 4],
    strokeWidth: [4],
  },
};

export const forecastChartOptionsValues: GetChartOptionsParamType = {
  type: 'rangeArea' as const,
  colors: chartConfigMapper[GraphTypesEnum.FORECAST_CHART].colors,
  fillOpacity: chartConfigMapper[GraphTypesEnum.FORECAST_CHART].fillOpacity,
  markersSize: chartConfigMapper[GraphTypesEnum.FORECAST_CHART].markersSize,
  strokeWidth: chartConfigMapper[GraphTypesEnum.FORECAST_CHART].strokeWidth,
  tooltipEnable: true,
};

export const lineChartOptionsValues: GetChartOptionsParamType = {
  type: 'line' as const,
  colors: chartConfigMapper[GraphTypesEnum.LINE_CHART].colors,
  fillOpacity: chartConfigMapper[GraphTypesEnum.LINE_CHART].fillOpacity,
  markersSize: chartConfigMapper[GraphTypesEnum.LINE_CHART].markersSize,
  strokeWidth: chartConfigMapper[GraphTypesEnum.LINE_CHART].strokeWidth,
  tooltipEnable: true,
};

export const anomalyChartOptionsValues: GetChartOptionsParamType = {
  type: 'rangeArea' as const,
  colors: chartConfigMapper[GraphTypesEnum.FORECAST_WITH_ANOMALY].colors,
  fillOpacity:
    chartConfigMapper[GraphTypesEnum.FORECAST_WITH_ANOMALY].fillOpacity,
  markersSize:
    chartConfigMapper[GraphTypesEnum.FORECAST_WITH_ANOMALY].markersSize,
  strokeWidth:
    chartConfigMapper[GraphTypesEnum.FORECAST_WITH_ANOMALY].strokeWidth,
  tooltipEnable: true,
  showLegend: true,
  hideCustomToolTip: true,
};

export const graphTypeConfigMapper = {
  [GraphTypesEnum.FORECAST_CHART]: [
    GraphDataNamesEnum.UPPER_THRESHOLD,
    GraphDataNamesEnum.GROUND_TRUTH,
    GraphDataNamesEnum.FORECAST_VALUE,
  ],
  [GraphTypesEnum.FORECAST_WITH_ANOMALY]: [
    GraphDataNamesEnum.UPPER_THRESHOLD,
    GraphDataNamesEnum.CRITICAL_BREACH,
    GraphDataNamesEnum.MAJOR_BREACH,
    GraphDataNamesEnum.MINOR_BREACH,
    GraphDataNamesEnum.GROUND_TRUTH,
    GraphDataNamesEnum.FORECAST_VALUE,
  ],
};

export const anomalySeverityMapper = {
  [GraphDataNamesEnum.CRITICAL_BREACH]: 3,
  [GraphDataNamesEnum.MAJOR_BREACH]: 2,
  [GraphDataNamesEnum.MINOR_BREACH]: 1,
};

const getSeriesInfo = (name: string, type: apexChartTypes) => ({
  name,
  type,
});

const chartSeriesInfoMapper = {
  [GraphDataNamesEnum.UPPER_THRESHOLD]: getSeriesInfo(
    i18n.global.t('confidence'),
    'rangeArea',
  ),
  [GraphDataNamesEnum.FORECAST_VALUE]: getSeriesInfo(
    i18n.global.t('forecastValue'),
    'line',
  ),
  [GraphDataNamesEnum.GROUND_TRUTH]: getSeriesInfo(
    i18n.global.t('groundTruth'),
    'line',
  ),
  [GraphDataNamesEnum.CRITICAL_BREACH]: getSeriesInfo(
    i18n.global.t('criticalBreach'),
    'scatter',
  ),
  [GraphDataNamesEnum.MAJOR_BREACH]: getSeriesInfo(
    i18n.global.t('majorBreach'),
    'scatter',
  ),
  [GraphDataNamesEnum.MINOR_BREACH]: getSeriesInfo(
    i18n.global.t('minorBreach'),
    'scatter',
  ),
};

export const generateSeries = (seriesMapper, graphItems) => {
  const series: any = [];
  for (let i = 0; i < graphItems.length; i++) {
    series.push({
      ...chartSeriesInfoMapper[graphItems[i]],
      data: seriesMapper[graphItems[i]],
    });
  }
  return series;
};

export const MIN_ZOOM_RANGE_LIMIT_FORECAST = 10 * 1000;

export const MIN_ZOOM_RANGE_LIMIT_LINE = 2;

export const ANNOTATION_RANGE = 120000;

export const ANNOTATION_FILL = '#D74654';

const TooltipFields = [
  {
    name: 'Current value',
  },
  {
    name: 'Confidence',
    split: true,
    lower: 'Lower threshold',
    upper: 'Upper threshold',
  },
];

const getFixedValue = (value, decimal) => {
  return parseFloat(value).toFixed(decimal);
};

export const getLocalTimeForGraphs = (timestamp, timeZoneOffset) => {
  const convertedTime = getLocalISOTime(timestamp, timeZoneOffset);
  return convertedTime;
};

export const customTooltip = ({ series, seriesIndex, dataPointIndex, w }) => {
  const timeStamp = w.globals.seriesX[seriesIndex][dataPointIndex];
  const points = w.globals.series.map((s, i) => {
    const currentSeries = w.config.series[i];
    const value = series[i][dataPointIndex];
    if (currentSeries.type === 'rangeArea') {
      if (currentSeries.data.length > 0) {
        const range = currentSeries.data[dataPointIndex]?.y;
        if (TooltipFields.some((obj) => obj.name === currentSeries.name)) {
          const field = TooltipFields.find(
            (obj) => obj.name === currentSeries.name,
          );
          if (field?.split) {
            return `<span><div>${field.lower}:  ${getFixedValue(range[0], 3)}</div><div>${field.upper}:  ${getFixedValue(range[1], 3)}</div></span>`;
          } else {
            return `<span>${currentSeries.name}:   ${getFixedValue(range[0], 3)} - ${getFixedValue(range[1], 3)}</span>`;
          }
        }
        return `<span></span>`;
      }
    } else {
      if (TooltipFields.some((obj) => obj.name === currentSeries.name)) {
        if (value) {
          return `<span>${currentSeries.name}:   ${getFixedValue(value, 3)}</span>`;
        }
      }
      return `<span></span>`;
    }
  });
  return `<div class="aiops-info-wrapper"><div class="aiops-info-header">${formatDate(timeStamp, 0, 'YYYY-MM-DD | HH:mm A')}</div><div class="aiops-info-body">${points.join('')}</div></div>`;
};

export const anomalyChartLegends: LegendInfoType[] = [
  {
    label: i18n.global.t('confidence'),
    key: GraphDataNamesEnum.UPPER_THRESHOLD,
    isActive: true,
    color: anomalyChartColors[0],
    type: 'line',
  },
  {
    label: i18n.global.t('criticalBreach'),
    key: GraphDataNamesEnum.CRITICAL_BREACH,
    isActive: true,
    color: anomalyChartColors[1],
    type: 'circle',
  },
  {
    label: i18n.global.t('majorBreach'),
    key: GraphDataNamesEnum.MAJOR_BREACH,
    isActive: true,
    color: anomalyChartColors[2],
    type: 'circle',
  },
  {
    label: i18n.global.t('minorBreach'),
    key: GraphDataNamesEnum.MINOR_BREACH,
    isActive: true,
    color: anomalyChartColors[3],
    type: 'circle',
  },
  {
    label: i18n.global.t('groundTruth'),
    key: GraphDataNamesEnum.GROUND_TRUTH,
    isActive: true,
    color: anomalyChartColors[4],
    type: 'line',
  },
  {
    label: i18n.global.t('forecastValue'),
    key: GraphDataNamesEnum.FORECAST_VALUE,
    isActive: true,
    color: anomalyChartColors[5],
    type: 'line',
  },
];

export const forecastChartRespFormat = (res) => {
  return res?.data?.data?.readAIMetricsValues?.data[0];
};

export const lineChartRespFormat = (res) => {
  return res?.data?.data?.readMetricsValues;
};

export const lineChartLabeledFormat = (res) => {
  return res?.data?.readLabeledMetricsValues;
};

export const respFormatter = (graphType) => {
  switch (graphType) {
    case GraphTypesEnum.FORECAST_CHART:
      return forecastChartRespFormat;
    case GraphTypesEnum.FORECAST_WITH_ANOMALY:
      return forecastChartRespFormat;
    case GraphTypesEnum.LINE_CHART:
      return lineChartRespFormat;
    case GraphTypesEnum.LINE_CHART_LABELLED:
      return lineChartLabeledFormat;
    default:
      return lineChartRespFormat;
  }
};

export const getChartType = (graphType) => {
  switch (graphType) {
    case GraphTypesEnum.FORECAST_CHART:
      return ChartTypesEnum.RANGE_AREA;
    case GraphTypesEnum.FORECAST_WITH_ANOMALY:
      return ChartTypesEnum.RANGE_AREA;
    case GraphTypesEnum.LINE_CHART:
      return ChartTypesEnum.LINE;
    default:
      return ChartTypesEnum.RANGE_AREA;
  }
};

export const getChartOptionsData = (graphType) => {
  switch (graphType) {
    case GraphTypesEnum.FORECAST_CHART:
      return toRef(() => forecastChartOptionsValues);
    case GraphTypesEnum.LINE_CHART:
      return toRef(() => lineChartOptionsValues);
    case GraphTypesEnum.FORECAST_WITH_ANOMALY:
      return toRef(() => anomalyChartOptionsValues);
    case GraphTypesEnum.LINE_CHART_LABELLED:
      return toRef(() => lineChartOptionsValues);
    default:
      return toRef(() => forecastChartOptionsValues);
  }
};

export const getZoomInRangeLimit = (graphType) => {
  return graphType === GraphTypesEnum.FORECAST_CHART ||
    graphType === GraphTypesEnum.FORECAST_WITH_ANOMALY
    ? MIN_ZOOM_RANGE_LIMIT_FORECAST
    : MIN_ZOOM_RANGE_LIMIT_LINE;
};

export const disableDatePickerOption = [
  timeLabels.oneWeek,
  timeLabels.oneMonth,
];
